<template>
  <div class="card shadow p-2 position-relative">
    <div
      class="position-absolute d-lg-none"
      style="max-width:100px; width: 100px; top: -10px"
    >
      <div
        class="badge badge-pill rounded-pill px-2"
        :class="`bg-${formattedStatus.color} text-${formattedStatus.textColor || 'blue-700'}`"
      >
        {{ formattedStatus.text }}
      </div>
    </div>
    <div class="card-body">
      <div
        class="d-flex flex-md-row flex-column
        justify-content-between w-100"
      >
        <div class="d-flex flex-column my-auto max-width-product-name">
          <div class="d-flex mb-2">
            <v-highlight
              :product-id="subscription.productId || null"
              :product-name="productName"
            />
          </div>
          <div class="d-flex lead">
            N° {{ subscription.reference }}
          </div>
          <div
            v-if="subscription.productId === 16"
            class="d-flex mt-2"
          >
            {{ formattedAddress }}
          </div>
          <div
            v-else-if="mainActivity"
            class="d-flex mt-2 text-truncate"
          >
            <span class="text-truncate">
              {{ mainActivity }}
            </span>
          </div>
        </div>
        <div
          class="d-flex flex-column my-lg-auto my-2 text-blue-700"
          style="max-width:120px; width: 120px"
        >
          <div
            v-if="formattedPrice"
            class="d-flex mb-2 fw-bold"
          >
            <v-icon
              class="my-auto d-md-none me-1"
              icon="money-bag"
            />
            {{ formattedPrice }} €
          </div>
          <div
            v-if="plan"
            class="d-flex mb-2"
          >
            <v-icon
              class="my-auto d-md-none me-1"
              icon="clock"
            />
            {{ plan }}
          </div>
          <div class="d-flex">
            <v-icon
              class="my-auto d-md-none me-1"
              icon="balloon"
              height="20"
              width="20"
            />
            {{ subscription.echeance }}
          </div>
        </div>
        <div
          class="my-auto text-center d-none d-lg-flex"
          style="max-width:100px; width: 100px"
        >
          <div
            class="badge badge-pill rounded-pill px-2"
            :class="`bg-${formattedStatus.color} text-${formattedStatus.textColor || 'blue-700'}`"
          >
            <div class="d-flex flex-row">
              <div>
                {{ formattedStatus.text }}
              </div>
              <v-icon
                :icon="formattedStatus.icon"
                height="10"
                width="10"
                class="my-auto ms-1"
              />
            </div>
          </div>
        </div>
        <div class="d-flex flex-column flex-md-row my-auto">
          <div :class="haveContractDocument ? 'd-md-flex my-auto' : 'invisible'">
            <v-download :action="downloadDocument" />
          </div>
          <v-loader
            id="contract-open-detail"
            :next="openContractDetail"
            title="Voir le détail"
            icon="arrow-white-right"
            class="my-auto text-nowrap w-100"
            :class="canOpenContractDetail ? '' : 'invisible'"
          />
          <v-loader
            :next="downloadDocument"
            color="blue-600"
            title="Télécharger"
            icon="download-white"
            class="d-md-none mt-2 w-100"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { has, get } from 'lodash-es';
import { mapGetters } from 'vuex';

import ProductHighlight from '@/components/common/helpers/ProductHighlight.vue';
import DownloadButton from '@/components/common/helpers/DownloadButton.vue';
import { getContractRouteId } from '@/utils/contractRoute';

export default {
  name: 'ContractDisplay',
  components: {
    'v-highlight': ProductHighlight,
    'v-download': DownloadButton,
  },
  props: {
    subscription: {
      type: Object,
      required: true,
      default: () => ({
        grid: { product: { name: null } },
      }),
    },
  },
  computed: {
    ...mapGetters({
      products: 'rater/products',
      invoices: 'invoices/items',
    }),
    productName() {
      return (this.products
        .find(({ id }) => id === this.subscription.productId)
        || {})
        .name;
    },
    formattedAddress() {
      if (!has(this.subscription, 'risk.local.address')) {
        return '';
      }
      const { address } = this.subscription.risk.local;
      return `${address.street}, ${address.city}`;
    },
    mainActivity() {
      return get(this.subscription, 'risk.mainActivity.name', null);
    },
    formattedStatus() {
      const status = [
        {
          id: 'ACTIVE',
          text: 'Actif',
          color: 'green-300',
          icon: 'checkmark',
        },
        {
          id: 'SUSPENDED',
          text: 'Suspendu',
          color: 'gray-300',
          icon: 'pause',
        },
        {
          id: 'CANCELLED',
          text: 'Résilié',
          color: 'pink-700',
          textColor: 'white',
          icon: 'times-white',
        },
      ];
      return status
        .find(({ id }) => id === this.subscription.status)
        || {};
    },
    plan() {
      const invoice = this.invoices
        .find(({ subscriptionId }) => subscriptionId === this.subscription.id);
      if (invoice) {
        return invoice.plan === 'MONTHLY' ? 'Mensuel' : 'Annuel';
      }
      return null;
    },
    formattedPrice() {
      const price = get(this.subscription, 'annualPremium', 0);
      return parseFloat(price)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        .replace('.', ',');
    },
    haveContractDocument() {
      return this.subscription.documents
        .some(({ type }) => type === 'POLICY');
    },
    canOpenContractDetail() {
      return this.subscription.delegation || this.subscription.visible;
    },
  },
  methods: {
    downloadDocument() {
      if (this.haveContractDocument) {
        return this.$store.dispatch('download/downloadPolicy', this.subscription);
      }
      return Promise.resolve();
    },
    async openContractDetail() {
      let contract = this.subscription;
      let routeId = getContractRouteId(contract);
      let entity = null;

      if (!contract.policyId && contract.id) {
        const response = await this.$store.dispatch('contracts/updateContract', contract.id);
        contract = response && response.data ? response.data : contract;
        routeId = getContractRouteId(contract);
      }

      if (!routeId) {
        return Promise.resolve();
      }

      if (!contract.policyId && !contract.activeVersionId) {
        entity = 'subscription';
      }

      await this.$router.push({
        path: `/client/contracts/${routeId}`,
        query: entity ? { entity } : {},
      });
      return Promise.resolve();
    },
  },
};
</script>
<style scoped>
@media screen and (min-width: 576px) {
  .max-width-product-name {
    width: 400px;
    max-width: 400px;
  }
}
</style>
